import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Select, Space, Typography } from 'antd';
import { useAppSelector } from '@store/store.tsx';
import type { SiteFontKey, SiteTypography } from '@store/slices/settingsSlice.tsx';
import {
  applySiteTypographyToDom,
  normalizeSiteTypography,
  SITE_FONT_OPTIONS,
} from '../typography/siteTypography';

const { Text, Title } = Typography;

export interface TypographyPanelRef {
  getValue: () => SiteTypography;
  markSaved: () => void;
  restore: () => void;
}

interface TypographyPanelProps {
  onDirtyChange?: (dirty: boolean) => void;
}

const fields: Array<{
  key: keyof SiteTypography;
  label: string;
  sample: string;
  level: 2 | 4 | 5;
}> = [
  { key: 'heading_font', label: 'Títulos', sample: 'Título principal', level: 4 },
  { key: 'subheading_font', label: 'Subtítulos', sample: 'Subtítulo de sección', level: 5 },
  { key: 'body_font', label: 'Párrafos y navegación', sample: 'Texto de ejemplo para comprobar su lectura.', level: 5 },
];

export const TypographyPanel = forwardRef<TypographyPanelRef, TypographyPanelProps>(
  ({ onDirtyChange }, ref) => {
    const remote = useAppSelector((state) => state.settings.site?.site_typography);
    const normalizedRemote = useMemo(() => normalizeSiteTypography(remote), [remote]);
    const [draft, setDraft] = useState<SiteTypography>(normalizedRemote);
    const [persisted, setPersisted] = useState<SiteTypography>(normalizedRemote);

    useEffect(() => {
      setDraft(normalizedRemote);
      setPersisted(normalizedRemote);
      applySiteTypographyToDom(normalizedRemote);
    }, [normalizedRemote]);

    useEffect(() => {
      onDirtyChange?.(JSON.stringify(draft) !== JSON.stringify(persisted));
    }, [draft, persisted, onDirtyChange]);

    useImperativeHandle(ref, () => ({
      getValue: () => draft,
      markSaved: () => setPersisted(draft),
      restore: () => {
        setDraft(persisted);
        applySiteTypographyToDom(persisted);
      },
    }), [draft, persisted]);

    const update = (key: keyof SiteTypography, value: SiteFontKey) => {
      setDraft((current) => {
        const next = { ...current, [key]: value };
        applySiteTypographyToDom(next);
        return next;
      });
    };

    return (
      <div className="layout-editor-typography" role="group" aria-label="Tipografía del sitio">
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {fields.map((field) => {
            const option = SITE_FONT_OPTIONS.find((item) => item.value === draft[field.key]);
            return (
              <div key={field.key} className="layout-editor-typography__field">
                <Text strong>{field.label}</Text>
                <Select
                  aria-label={`Fuente para ${field.label.toLowerCase()}`}
                  value={draft[field.key]}
                  options={SITE_FONT_OPTIONS.map(({ label, value }) => ({ label, value }))}
                  onChange={(value) => update(field.key, value)}
                  style={{ width: '100%', marginTop: 6 }}
                />
                {field.key === 'body_font' ? (
                  <Text
                    className="layout-editor-typography__sample"
                    style={{ fontFamily: option?.family }}
                  >
                    {field.sample}
                  </Text>
                ) : (
                  <Title
                    level={field.level}
                    className="layout-editor-typography__sample"
                    style={{ fontFamily: option?.family }}
                  >
                    {field.sample}
                  </Title>
                )}
              </div>
            );
          })}
        </Space>
      </div>
    );
  },
);

TypographyPanel.displayName = 'TypographyPanel';
